import type {
  FilterParam,
  FilterParams,
} from '@/modules/common/context/QueryParams/models';
import type {UseFilterParams} from '@/modules/common/hooks/useFilterParams';
import type {Filter} from '@/modules/common/models/filters';

import {pick, some, sortBy} from 'lodash';

import {config} from '@/config';
import {
  FILTER_PARAMS_HOTELS,
  TravelTypes,
} from '@/modules/common/context/QueryParams/models';
import {
  SEA_CAMPAIGN_URL_PARAM,
  useSeaCampaign,
} from '@/modules/common/context/SeaCampaign';
import {useLocalSplit} from '@/modules/common/hooks';
import {useInventoryFilters} from '@/modules/common/hooks/filter/useInventoryFilters';
import {useMetaFilters} from '@/modules/common/hooks/filter/useMetaFilters';
import {usePriceFilter} from '@/modules/common/hooks/filter/usePriceFilter';
import {useFilterParams} from '@/modules/common/hooks/useFilterParams';
import {useSearchParams} from '@/modules/common/hooks/useSearchParams';
import {
  FILTER_DISTANCE_CITY_CENTER,
  FILTER_DISTANCE_SKI_SLOPE,
  FILTER_DISTANCE_AIRPORT,
  FILTER_DISTANCE_BEACH,
  FILTER_BEACH,
  FILTER_BOARD_TYPE,
  FILTER_CHILDREN,
  FILTER_ENTERTAINMENT,
  FILTER_FACILITIES,
  FILTER_FLIGHT_TRANSFER,
  FILTER_HOTEL_CATEGORY,
  FILTER_HOTEL_RATING,
  FILTER_HOTEL_TYPE,
  FILTER_POOL,
  FILTER_PRICE,
  FILTER_PROMOTION_FLEX,
  FILTER_PROMOTION_SAFETY,
  FILTER_PROMOTION_SPECIALS,
  FILTER_RESTAURANT,
  FILTER_ROOM_ATTRIBUTES,
  FILTER_ROOM_TYPE,
  FILTER_SPORTS,
  FILTER_SUSTAINABILITY,
  FILTER_TOUR_OPERATOR,
  FILTER_WATER_SPORTS,
  FILTER_WELLNESS,
} from '@/modules/common/models/filters';
import {useAppSelector} from '@/redux/config';
import {
  getInventoryFiltered,
  getInventoryUnfiltered,
  isInventoryLoaded as selectIsInventoryLoaded,
} from '@/redux/v1/hotels/inventory/reducer';
import {
  getMetaFiltered,
  getMetaUnfiltered,
  areHotelsLoaded as selectAreHotelsLoaded,
} from '@/redux/v1/hotels/list/reducer';

export const useFilters = (): {
  filters: Array<Filter>;
  hasFilters: boolean;
  currentFilterParams: Partial<FilterParams>;
  isReady: boolean;
  set: (...args: any) => void;
  clear: () => void;
} => {
  const [searchParams] = useSearchParams();
  const {rooms} = searchParams;
  const seaCampaign = useSeaCampaign();
  const distanceFiltersEnabled =
    useLocalSplit('FFP_5584_distance_filters').treatment === 'on';

  const [filterParams, baseSetFilterParams] = useFilterParams();
  // Override some methods to handle deactivation of potential SEA campaign entries.
  // SEA is special in that:
  // - we add a new history entry instead of updating
  // - we remove the SEA URL parameter
  //
  // @FIXME re-evaluate this solution...
  const setFilterParams: UseFilterParams[1] = (init, options) => {
    baseSetFilterParams(
      (previous) => ({
        ...init(previous),
        [SEA_CAMPAIGN_URL_PARAM]: null,
      }),
      {
        replace: !seaCampaign.active,
        ...options,
      }
    );
  };
  const filterParamsPatched = [filterParams, setFilterParams] as const;

  const currentFilterParams = pick<FilterParams, FilterParam>(
    filterParams,
    FILTER_PARAMS_HOTELS
  );
  const hasFilters = some(currentFilterParams, (value) =>
    Array.isArray(value)
      ? value.length > 0
      : value !== null && value !== undefined
  );

  const clear = () => {
    setFilterParams((previous) => ({
      ...previous,
      ...Object.fromEntries(FILTER_PARAMS_HOTELS.map((param) => [param, null])),
    }));
  };

  const metaUnfiltered = useAppSelector(getMetaUnfiltered);
  const metaFiltered = useAppSelector(getMetaFiltered);
  const inventoryUnfiltered = useAppSelector(getInventoryUnfiltered);
  const inventoryFiltered = useAppSelector(getInventoryFiltered);

  const areHotelsLoaded = useAppSelector(selectAreHotelsLoaded);
  const isInventoryLoaded = useAppSelector(selectIsInventoryLoaded);
  const isReady = areHotelsLoaded && isInventoryLoaded;

  const enabledFilters: Partial<Record<Filter['id'], boolean>> = {
    [FILTER_PROMOTION_FLEX]: true,
    [FILTER_PROMOTION_SPECIALS]: true,
    [FILTER_PROMOTION_SAFETY]: true,
    [FILTER_PRICE]:
      config.filters.price.enabledWithMultiroom ||
      searchParams.rooms.length === 1,
    [FILTER_HOTEL_CATEGORY]: true,
    [FILTER_HOTEL_RATING]: true,
    [FILTER_FLIGHT_TRANSFER]:
      searchParams.travelType === TravelTypes.packageTour,
    [FILTER_BOARD_TYPE]: true,
    [FILTER_ROOM_TYPE]: true,
    [FILTER_ROOM_ATTRIBUTES]: true,
    [FILTER_BEACH]: true,
    [FILTER_HOTEL_TYPE]: true,
    [FILTER_FACILITIES]: true,
    [FILTER_POOL]: true,
    [FILTER_CHILDREN]: true,
    [FILTER_SUSTAINABILITY]: true,
    [FILTER_DISTANCE_AIRPORT]: distanceFiltersEnabled,
    [FILTER_DISTANCE_BEACH]: distanceFiltersEnabled,
    [FILTER_DISTANCE_CITY_CENTER]: distanceFiltersEnabled,
    [FILTER_DISTANCE_SKI_SLOPE]: distanceFiltersEnabled,
    [FILTER_SPORTS]: true,
    [FILTER_WATER_SPORTS]: true,
    [FILTER_WELLNESS]: true,
    [FILTER_RESTAURANT]: true,
    [FILTER_ENTERTAINMENT]: true,
    [FILTER_TOUR_OPERATOR]: true,
  };

  const displayPriority = Object.keys(enabledFilters) as Array<Filter['id']>;

  const filters = sortBy(
    [
      ...useMetaFilters({
        filterParams: filterParamsPatched,
        metaUnfiltered,
        metaFiltered,
        rooms,
      }),
      ...useInventoryFilters({
        filterParams: filterParamsPatched,
        inventoryUnfiltered,
        inventoryFiltered,
        rooms,
      }),
      usePriceFilter({
        filterParams: filterParamsPatched,
        priceMin: metaFiltered?.priceRange?.minDisplay,
      }),
    ].filter((filter) => enabledFilters[filter.id as Filter['id']] === true),
    (filter: Filter) => displayPriority.indexOf(filter.id)
  );

  return {
    // @ts-expect-error ...
    filters,
    hasFilters,
    currentFilterParams,
    isReady,
    set: setFilterParams,
    clear,
  };
};
